import Importer from '../exporter/Importer.js'
import { getAdapter, getPackageableAdapters } from '../stores/resourceRegistry.js'

/**
 * Loads a DesignPackage into the live editor session — transactionally (see
 * docs plan "Design Packages" § "DesignPackageLoader"). Resources are
 * validated, then committed into their owning stores; only once every
 * resource has succeeded is the live layer/canvas touched at all. If any
 * resource fails to commit, everything newly registered so far is rolled
 * back and the layer is left exactly as it was before the import started.
 *
 * @param {import('./DesignPackage').DesignPackage} pkg
 * @param {object} layer     - the live Konva layer
 * @param {function} callback - progress callback, same shape Importer expects
 * @returns {Promise<{versionHistory: array, guidelines: array, bookmarks: object, datasets: array}>}
 *          the non-widget parts of design.json, for the caller to restore into its own session state
 */
export async function loadDesignPackage(pkg, layer, callback) {
    const designJson = pkg.getDesignJson();
    if (!designJson) throw new Error(`Package is missing ${pkg.manifest.entry || 'design.json'}`);

    // 1. Validate every packaged resource file first. Nothing is registered,
    // nothing on the live layer is touched, until every file here passes.
    const toCommit = []; // { adapter, entry }
    const audioAdapter = getAdapter('audio')
    const audioStreams = designJson.audioStreams ?? []
    const audioSynths = designJson.audioSynths ?? []
    audioAdapter?.validateExternalResources?.(audioStreams)
    audioAdapter?.validateSynthReferences?.(audioSynths)
    for (const adapter of getPackageableAdapters()) {
        for (const [filename, file] of pkg.filesUnder(adapter.packageFolder)) {
            const entry = { filename, content: file.content, encoding: file.encoding, mime: file.mime };
            adapter.validatePackageFile(entry); // throws on bad data — aborts before anything is committed
            toCommit.push({ adapter, entry });
        }
    }

    // 2. Commit resources, tracking every newly-created one so a later
    // failure can be rolled back without touching resources that were
    // already in the library (deduped, not newly created). Also track any
    // uuid the commit changed (a fresh create mints a new uuid; a dedup may
    // resolve to a pre-existing resource under a different uuid) — widgets in
    // design.json still reference the uuid they were exported under, and some
    // resource kinds (images) have no other way to resolve a reference, so
    // those need remapping before the layer is touched (see step 3).
    const created = []; // { adapter, uuid }
    const remaps = [];  // { adapter, oldUuid, newUuid }
    try {
        for (const { adapter, entry } of toCommit) {
            const result = await adapter.commitPackageFile(entry);
            if (result?.created && result.uuid != null) {
                created.push({ adapter, uuid: result.uuid });
            }
            const oldUuid = adapter.originalUuid?.(entry);
            if (oldUuid && result?.uuid && oldUuid !== result.uuid) {
                remaps.push({ adapter, oldUuid, newUuid: result.uuid });
            }
        }
        const restoredStreams = audioAdapter?.restoreExternalResources?.(audioStreams)
        for (const uuid of restoredStreams?.created ?? []) {
            created.push({ adapter: audioAdapter, uuid })
        }
        for (const { oldUuid, newUuid } of restoredStreams?.remaps ?? []) {
            remaps.push({ adapter: audioAdapter, oldUuid, newUuid })
        }
        const restoredSynths = audioAdapter?.restoreSynthReferences?.(audioSynths)
        for (const uuid of restoredSynths?.created ?? []) {
            created.push({ adapter: audioAdapter, uuid })
        }
        for (const { oldUuid, newUuid } of restoredSynths?.remaps ?? []) {
            remaps.push({ adapter: audioAdapter, oldUuid, newUuid })
        }
    } catch (error) {
        for (const { adapter, uuid } of created) {
            try { await adapter.remove(uuid); } catch { /* best-effort rollback */ }
        }
        throw error;
    }

    // 3. Rewrite widget references for any resource whose uuid changed on
    // import, then — only now — touch the live layer/canvas.
    for (const { adapter, oldUuid, newUuid } of remaps) {
        adapter.remapReference?.(designJson.objects ?? [], oldUuid, newUuid);
    }
    const importer = new Importer();
    await importer.importParsedDesign(designJson, layer, callback);
    const connectionSpacing = (designJson.objects ?? [])
        .find(item => item.page !== undefined && item.id === undefined && item.connection_spacing !== undefined)
        ?.connection_spacing;

    return {
        id: designJson.id ?? pkg.manifest.id ?? null,
        versionHistory: designJson.versionHistory ?? [],
        guidelines: designJson.guidelines ?? [],
        bookmarks: designJson.bookmarks ?? [],
        datasets: designJson.datasets ?? [],
        connectionSpacing,
    };
}
